/**
 * Inclusão rápida de itens (v6): UM formulário de item. Enter valida só este item e o inclui
 * na tabela; o formulário limpa e o foco volta ao Produto. O botão "Incluir produto" faz o
 * mesmo para quem usa o mouse; uma confirmação discreta mostra o que entrou.
 *
 * Contra inclusão dupla: o Enter da lista de sugestões é do lookup (não chega aqui); teclas
 * repetidas e composição de acento são ignoradas; cada rascunho tem um `key` próprio e a
 * tabela recusa o mesmo `key` duas vezes; enquanto uma inclusão está em curso, outra espera.
 */
import {
  formatMoney,
  multiplyDecimal,
  saleItemWarnings,
  saleLineTotal,
  toFormMoney,
  toFormNumber,
  validateSaleEntry,
  type ProductListRow,
  type SaleDraftItem,
  type SaleEntryField,
} from "@cafegestor/shared";
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { Icon } from "@/components/icons";
import { Button, Select } from "@/components/ui";
import { DecimalInput } from "@/components/DecimalInput";
import { newIdempotencyKey } from "@/lib/api";
import { useCadastroOptions } from "../../cadastros/lib";
import { qty, unitsOf, useProductDetail } from "../../estoque/lib";
import { ProductPicker } from "../../estoque/pickers";
import { usePriceLookup } from "../lib";

type Entry = {
  key: string;
  productId: string;
  productLabel: string;
  productCode: string;
  productName: string;
  unitId: string;
  quantity: string;
  unitPrice: string;
  discount: string;
};

const blank = (): Entry => ({
  key: newIdempotencyKey(),
  productId: "",
  productLabel: "",
  productCode: "",
  productName: "",
  unitId: "",
  quantity: "",
  unitPrice: "",
  discount: "",
});

/** Item da tabela de volta ao formulário (edição). */
const fromItem = (i: SaleDraftItem): Entry => ({
  key: i.key,
  productId: i.productId,
  productLabel: `${i.productCode} — ${i.productName}`,
  productCode: i.productCode,
  productName: i.productName,
  unitId: i.unitId,
  quantity: toFormNumber(i.quantity),
  unitPrice: i.unitPrice === null ? "" : toFormNumber(i.unitPrice),
  discount: Number(i.discount) === 0 ? "" : toFormMoney(i.discount),
});

export type AddOutcome = "incluido" | "somado" | "nova_linha" | "repetido" | "atualizado";

export function QuickItemEntry({
  customerId,
  warehouseId,
  editing,
  onAdd,
  onUpdate,
  onCancelEdit,
  onNeedCustomer,
  disabled,
  canSellShort = false,
}: {
  customerId: string;
  warehouseId: string;
  /** item da tabela sendo editado (Enter numa linha) */
  editing: SaleDraftItem | null;
  onAdd: (item: SaleDraftItem) => AddOutcome;
  onUpdate: (key: string, item: SaleDraftItem) => void;
  onCancelEdit: () => void;
  /** sem cliente não há preço: leva o foco ao cliente */
  onNeedCustomer: () => void;
  disabled?: boolean;
  /** v7: quem pode vender sem estoque vê um aviso informativo (não bloqueia) */
  canSellShort?: boolean;
}) {
  const [e, setE] = useState<Entry>(blank);
  const [errors, setErrors] = useState<Partial<Record<SaleEntryField, string>>>({});
  const [message, setMessage] = useState("");
  const [announce, setAnnounce] = useState("");
  // confirmação visível da última inclusão (some sozinha)
  const [added, setAdded] = useState<string | null>(null);
  useEffect(() => {
    if (!added) return;
    const id = setTimeout(() => setAdded(null), 2500);
    return () => clearTimeout(id);
  }, [added]);
  const busy = useRef(false);
  const productRef = useRef<HTMLInputElement | null>(null);
  const qtyRef = useRef<HTMLInputElement | null>(null);
  const unitRef = useRef<HTMLSelectElement | null>(null);
  const priceRef = useRef<HTMLInputElement | null>(null);
  const discountRef = useRef<HTMLInputElement | null>(null);

  // edição de um item da tabela: carrega no formulário quando o item em edição muda
  const editingKey = editing?.key ?? null;
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  if (editingKey !== loadedKey) {
    setLoadedKey(editingKey);
    if (editing) {
      setE(fromItem(editing));
      setErrors({});
      setMessage("");
    }
  }
  useEffect(() => {
    if (editingKey) qtyRef.current?.focus();
  }, [editingKey]);

  const product = useProductDetail(e.productId);
  const options = useCadastroOptions();
  const units = unitsOf(product.data);
  const unit = units.find((u) => u.id === e.unitId);
  const unitDecimals = options.data?.units.find((u) => u.id === e.unitId)?.decimals ?? 6;
  // unidade de venda: a embalagem marcada para venda, senão a unidade-base
  useEffect(() => {
    if (!product.data || e.unitId) return;
    const sale = product.data.packagings?.find((p) => p.isSaleUnit && p.status === "active");
    setE((x) => (x.unitId ? x : { ...x, unitId: sale?.unitId ?? product.data!.baseUnitId }));
  }, [product.data, e.unitId]);

  const price = usePriceLookup({
    customerId,
    productId: e.productId,
    unitId: e.unitId,
    warehouseId,
  });
  const priceInfo = price.data
    ? { listPrice: price.data.listPrice, maxDiscountPercent: price.data.maxDiscountPercent }
    : undefined;
  const avail = price.data?.availability ?? null;

  const draft = validateSaleEntry({
    key: e.key,
    productId: e.productId,
    productCode: e.productCode,
    productName: e.productName,
    unitId: e.unitId,
    unitCode: unit?.code ?? "",
    unitDecimals,
    quantity: e.quantity,
    unitPrice: e.unitPrice,
    discount: e.discount,
    priceInfo,
  });
  const ready = draft.ok ? draft.item : null;
  const quantityBase = ready && unit ? multiplyDecimal(ready.quantity, unit.factor) : undefined;
  const warnings = ready
    ? saleItemWarnings(ready, avail?.available ?? null, quantityBase, {
        canSellShort: canSellShort && product.data?.strictTraceability !== true,
        unitCode: product.data?.baseUnitCode ?? "",
      })
    : [];
  const total = ready ? saleLineTotal(ready) : null;
  // preço da tabela ainda sendo buscado: incluir espera (o botão mostra que está processando)
  const pricing = price.isFetching && !price.data;

  function focusField(f: SaleEntryField) {
    const map = {
      productId: productRef,
      unitId: unitRef,
      quantity: qtyRef,
      unitPrice: priceRef,
      discount: discountRef,
    };
    map[f].current?.focus();
  }

  function include() {
    if (busy.current || disabled) return;
    if (!customerId) {
      setMessage("Escolha o cliente antes de incluir produtos (o preço vem da tabela dele).");
      onNeedCustomer();
      return;
    }
    if (draft.ok === false) {
      if (draft.pending) {
        setMessage("Buscando o preço da tabela… confirme de novo em seguida.");
        return;
      }
      setErrors(draft.errors);
      setMessage("");
      focusField(draft.first);
      return;
    }
    busy.current = true;
    try {
      const item = draft.item;
      let outcome: AddOutcome;
      if (editing) {
        onUpdate(editing.key, { ...item, key: editing.key });
        outcome = "atualizado";
      } else {
        outcome = onAdd(item);
      }
      const line = `${item.productCode}, ${qty(item.quantity, item.unitCode)}${
        total ? `, ${formatMoney(total)}` : ""
      }`;
      setAdded(
        outcome === "repetido"
          ? null
          : outcome === "somado"
            ? `Somado: ${line}`
            : outcome === "atualizado"
              ? `Alterado: ${line}`
              : `Incluído: ${line}`,
      );
      setAnnounce(
        outcome === "somado"
          ? `Quantidade somada ao item já incluído: ${line}`
          : outcome === "atualizado"
            ? `Item alterado: ${line}`
            : outcome === "repetido"
              ? ""
              : `Item incluído: ${line}`,
      );
      setMessage(
        outcome === "nova_linha"
          ? "Produto já está no pedido em outra condição: incluído em nova linha."
          : "",
      );
      setE(blank());
      setErrors({});
      if (editing) onCancelEdit();
      productRef.current?.focus();
    } finally {
      busy.current = false;
    }
  }

  function onKeyDown(ev: KeyboardEvent<HTMLDivElement>) {
    if (ev.key === "Escape") {
      ev.preventDefault();
      setE(blank());
      setErrors({});
      setMessage("");
      if (editing) onCancelEdit();
      productRef.current?.focus();
      return;
    }
    if (ev.key !== "Enter" || ev.defaultPrevented) return;
    if (ev.ctrlKey || ev.metaKey) return; // Ctrl+Enter é "Salvar rascunho"
    if (ev.repeat || ev.nativeEvent.isComposing) return;
    if ((ev.target as HTMLElement).tagName === "BUTTON") return; // o botão cuida do clique
    ev.preventDefault();
    // produto vazio: Enter não faz nada (não grava o pedido)
    if (!e.productId && e.quantity.trim() === "") return;
    include();
  }

  const set = (patch: Partial<Entry>) => {
    setE((x) => ({ ...x, ...patch }));
    const fields = Object.keys(patch) as (keyof Entry)[];
    setErrors((er) => {
      const next = { ...er };
      for (const f of fields) delete next[f as SaleEntryField];
      return next;
    });
  };
  const err = (f: SaleEntryField) =>
    errors[f] ? (
      <span id={`entrada-${f}-erro`} className="mt-1 block text-xs text-destructive">
        {errors[f]}
      </span>
    ) : null;
  const invalid = (f: SaleEntryField) =>
    errors[f] ? { "aria-invalid": true as const, "aria-describedby": `entrada-${f}-erro` } : {};

  const hint = !e.productId
    ? "Digite o código ou a descrição; Enter escolhe; F2 abre a lupa."
    : [
        priceInfo === undefined
          ? customerId
            ? "Buscando preço…"
            : "Escolha o cliente para ver o preço"
          : priceInfo.listPrice === null
            ? "Fora da tabela do cliente: informe o preço"
            : `Tabela ${formatMoney(priceInfo.listPrice)}${
                priceInfo.maxDiscountPercent
                  ? ` · desc. máx. ${toFormNumber(priceInfo.maxDiscountPercent)}%`
                  : ""
              }`,
        avail ? `disponível ${qty(avail.available, product.data?.baseUnitCode)}` : null,
        total ? `total da linha ${formatMoney(total)}` : null,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <div onKeyDown={onKeyDown} className="@container space-y-3">
      {/* largura real da área: produto em linha inteira no celular; lado a lado a partir de ~42rem */}
      <div className="grid grid-cols-2 items-start gap-x-3 gap-y-3 @2xl:grid-cols-12">
        <label
          className="col-span-2 block min-w-0 text-sm @2xl:col-span-12 @4xl:col-span-5"
          htmlFor="entrada-produto"
        >
          <span className="font-medium text-foreground">Produto</span>
          <div className="mt-1.5">
            <ProductPicker
              id="entrada-produto"
              inputRef={productRef}
              value={e.productId}
              label={e.productLabel}
              sale={{ warehouseId, customerId }}
              {...(disabled !== undefined ? { disabled } : {})}
              {...(errors.productId ? { invalid: true } : {})}
              onChange={(id, label, p?: ProductListRow) =>
                set({
                  productId: id,
                  productLabel: label,
                  productCode: p?.code ?? "",
                  productName: p?.name ?? "",
                  unitId: "",
                  unitPrice: "",
                })
              }
              onSelected={() => qtyRef.current?.focus()}
            />
          </div>
          {err("productId")}
        </label>
        <label className="block min-w-0 text-sm @2xl:col-span-3 @4xl:col-span-1">
          <span className="font-medium text-foreground">
            Un.<span className="sr-only"> (unidade)</span>
          </span>
          <Select
            ref={unitRef}
            className="mt-1.5 px-2"
            title={unit?.code}
            value={e.unitId}
            disabled={disabled || units.length === 0}
            {...invalid("unitId")}
            onChange={(ev) => set({ unitId: ev.target.value, unitPrice: "" })}
          >
            {units.length === 0 && <option value="">—</option>}
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
          {err("unitId")}
        </label>
        <label className="block min-w-0 text-sm @2xl:col-span-3 @4xl:col-span-2">
          <span className="font-medium text-foreground">Quantidade</span>
          <DecimalInput
            inputRef={qtyRef}
            className="mt-1.5"
            mode="numero"
            places={undefined}
            value={e.quantity}
            {...(disabled !== undefined ? { disabled } : {})}
            {...(errors.quantity ? { invalid: true, describedBy: "entrada-quantity-erro" } : {})}
            onChange={(ev) => set({ quantity: ev.target.value })}
          />
          {err("quantity")}
        </label>
        <label className="block min-w-0 text-sm @2xl:col-span-3 @4xl:col-span-2">
          <span className="font-medium text-foreground">Preço unit. (R$)</span>
          <DecimalInput
            inputRef={priceRef}
            className="mt-1.5"
            places={undefined}
            value={e.unitPrice}
            placeholder={priceInfo?.listPrice ? toFormMoney(priceInfo.listPrice) : ""}
            {...(disabled !== undefined ? { disabled } : {})}
            {...(errors.unitPrice ? { invalid: true, describedBy: "entrada-unitPrice-erro" } : {})}
            onChange={(ev) => set({ unitPrice: ev.target.value })}
          />
          {err("unitPrice")}
        </label>
        <label className="block min-w-0 text-sm @2xl:col-span-3 @4xl:col-span-2">
          <span className="font-medium text-foreground">Desconto (R$)</span>
          <DecimalInput
            inputRef={discountRef}
            className="mt-1.5"
            value={e.discount}
            {...(disabled !== undefined ? { disabled } : {})}
            {...(errors.discount ? { invalid: true, describedBy: "entrada-discount-erro" } : {})}
            onChange={(ev) => set({ discount: ev.target.value })}
          />
          {err("discount")}
        </label>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          // ação principal da etapa: marrom, com ícone; Enter em qualquer campo faz o mesmo
          disabled={disabled || !e.productId || pricing}
          aria-busy={pricing || undefined}
          aria-keyshortcuts="Enter"
          onClick={include}
          title="Também com Enter em qualquer campo do item"
          className="min-w-44"
        >
          {pricing ? (
            <span
              aria-hidden
              className="size-4 animate-spin rounded-full border-2 border-white/40 border-t-white"
            />
          ) : (
            <Icon name={editing ? "check" : "plus"} size={18} strokeWidth={2.2} />
          )}
          {editing ? "Atualizar produto" : "Incluir produto"}
          <kbd
            aria-hidden
            className="ml-1 rounded border border-white/30 px-1.5 py-px font-sans text-[11px] font-medium text-white/85"
          >
            Enter
          </kbd>
        </Button>
        {editing && (
          <Button
            variant="ghost"
            onClick={() => {
              setE(blank());
              setErrors({});
              onCancelEdit();
            }}
          >
            Cancelar edição (Esc)
          </Button>
        )}
        <span className="min-w-0 text-sm text-muted-foreground">{hint}</span>
        {added && (
          <span
            aria-hidden
            className="inline-flex items-center gap-1.5 rounded-full bg-success-soft px-2.5 py-1 text-xs font-semibold text-success-strong"
          >
            <Icon name="check" size={14} strokeWidth={2.4} />
            {added}
          </span>
        )}
      </div>
      {warnings.length > 0 && (
        <ul className="list-disc rounded-md bg-warning/15 py-2 pl-8 pr-3 text-sm">
          {warnings.map((w) => (
            <li key={w}>{w}</li>
          ))}
        </ul>
      )}
      {message && <p className="text-sm text-muted-foreground">{message}</p>}
      <p role="status" aria-live="polite" className="sr-only">
        {announce}
      </p>
    </div>
  );
}
